import {getCountryCode} from '@smart/locale-service';
import * as React from 'react';
import {useEffect, useRef} from 'react';
import {getCookie, setCookie} from '../common/helpers';
import type {IViewStateMapping, ViewsFetchingToPostForm} from '../common/types';
import {postFormToEndpoint} from '../utils/postFormToEndpoint';
import {ECID} from './use-ecid';
import {useFeatureAppEnvHook} from './use-feature-app-env';
import {useLocale} from './use-locale';

const cookieKey = `ECIDsubmitted`;

export function useAPI<T extends ViewsFetchingToPostForm>(
  view: T,
  addECID: boolean = false,
) {
  const cancelRef = useRef<AbortController | null>(null);
  useEffect(() => {
    return () => cancelRef.current?.abort();
  }, []);
  const {marketId, languageTag} = useLocale();
  const countryCode = getCountryCode(marketId);
  const {baseUrl, featureServices} = useFeatureAppEnvHook();
  const {'endpoint-directory': endpointDirectory} = featureServices;

  const fetchBFF = React.useCallback(async (body: IViewStateMapping<{}>[T]) => {
    if (cancelRef.current) cancelRef.current.abort();

    const shouldFetchECID = addECID && getCookie(cookieKey) == null;
    const ecid = shouldFetchECID && (await ECID());

    const abortController = new AbortController();
    cancelRef.current = abortController;

    const result = await postFormToEndpoint(
      baseUrl,
      endpointDirectory,
      {
        countryCode,
        marketId,
        languageTag,
      },
      {
        view,
        payload: {
          ...(ecid ? {ecid} : {}),
          ...body,
          market: countryCode,
        },
      },
      abortController.signal,
    );

    cancelRef.current = null;
    if (ecid) setCookie(cookieKey, `true`, 180 /* six months */);
    return result;
  }, []);

  return fetchBFF;
}
